import { environment } from '../../../environments/environment';

/** localStorage: UI language the visitor chose in the header. */
export const MANUAL_LANG_STORAGE_KEY = 'lang';

const FALLBACK_API_LANG = 'en';
const FALLBACK_UI_LANG = 'en';

const UI_TO_API: Record<string, string> = {
  en: 'en',
  norway: 'no',
  sv: 'sl',
  spain: 'es',
  swedan: 'sw',
  sw: 'sw',
  da: 'da',
  cs: 'cs',
  it: 'it',
  fr: 'fr',
  de: 'de',
};

const API_TO_UI: Record<string, string> = {
  en: 'en',
  no: 'norway',
  sl: 'sv',
  es: 'spain',
  sw: 'swedan',
  da: 'da',
  cs: 'cs',
  it: 'it',
  fr: 'fr',
  de: 'de',
};

const ALLOWED_API_LANGS = new Set(Object.values(UI_TO_API));

function readEnvFlag(key: string): unknown {
  return (environment as Record<string, unknown>)[key];
}

function isClientLanguageEnabled(): boolean {
  return readEnvFlag('is_client_language_enabled') === true;
}

function getEnvLanguageCode(): string | undefined {
  const value = readEnvFlag('language');
  return typeof value === 'string' ? value : undefined;
}

function normalizeApiLang(code: string | null | undefined): string {
  const trimmed = code?.trim() ?? '';
  if (!trimmed) {
    return FALLBACK_API_LANG;
  }
  if (ALLOWED_API_LANGS.has(trimmed)) {
    return trimmed;
  }
  const fromUi = UI_TO_API[trimmed];
  return fromUi ?? FALLBACK_API_LANG;
}

/** ngx-translate / header codes (`norway`, `spain`, …). */
export function apiLangToUiLang(apiLang: string): string {
  return API_TO_UI[normalizeApiLang(apiLang)] ?? FALLBACK_UI_LANG;
}

export function uiLangToApiLang(uiLang: string): string {
  return UI_TO_API[uiLang] ?? (ALLOWED_API_LANGS.has(uiLang) ? uiLang : FALLBACK_API_LANG);
}

export function getEnvDefaultApiLanguage(): string {
  if (!isClientLanguageEnabled()) {
    return FALLBACK_API_LANG;
  }
  return normalizeApiLang(getEnvLanguageCode());
}

export function getEnvDefaultUiLanguage(): string {
  return apiLangToUiLang(getEnvDefaultApiLanguage());
}

export function getManualUiLanguage(): string | null {
  const storage = globalThis.window?.localStorage;
  if (!storage) {
    return null;
  }
  const saved = storage.getItem(MANUAL_LANG_STORAGE_KEY)?.trim();
  return saved ? saved : null;
}

export function persistManualUiLanguage(uiLang: string): void {
  const storage = globalThis.window?.localStorage;
  if (!storage) {
    return;
  }
  storage.setItem(MANUAL_LANG_STORAGE_KEY, uiLang);
}

/** API `language` / `Accept-Language`: manual pick wins, else env default or `en`. */
export function resolveApiLanguage(): string {
  const manualUi = getManualUiLanguage();
  if (manualUi) {
    return uiLangToApiLang(manualUi);
  }
  return getEnvDefaultApiLanguage();
}

/** ngx-translate code: manual pick wins, else env default or `en`. */
export function resolveUiLanguage(): string {
  return getManualUiLanguage() ?? getEnvDefaultUiLanguage();
}
